/**
 * Alpine-компонент registrationWizard — единый flow регистрации.
 *
 * Заменяет 5 FancyBox-модалок (register-step1/step2-regular/step2-designer/step3/step3-finish)
 * на одну native <dialog>-модалку с x-show по currentStep.
 *
 * Backend endpoints (см. backend-frontend-contract.md):
 *   POST /register/step1     — выбор типа (rate-limit 10/min)
 *   POST /register/step2     — данные формы (rate-limit 10/min, 422 validation)
 *   POST /register/send-code — отправка SMS OTP (rate-limit 3/10min, код не возвращается)
 *   POST /register/resend    — повторная отправка SMS (rate-limit 3/10min)
 *   POST /register/verify    — проверка кода (rate-limit 10/min)
 *
 * Все 429 (Too Many Requests) перехватываются apiRequest → RateLimitError с retryAfter,
 * показываются toast-ом с задержкой.
 *
 * Шаги:
 *   1 — выбор типа пользователя (regular/designer)
 *   2 — форма данных (разные поля в зависимости от userType)
 *   3 — SMS-код подтверждения
 *   4 — финальный экран с поздравлением и редиректом
 */

import Alpine from 'alpinejs';
import { apiRequest, APIError } from '../../api.js';
import { openDialog, closeDialog, focusFirstInput } from '../../dialog-utils.js';

const RESEND_TIMER_SECONDS = 180;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_REGEX = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

function emptyFormData() {
    return {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        // DEV-993: iso2 код страны из intl-tel-input. Без него бэкенд (PhoneHelper::toE164)
        // не может достроить E.164 и номер на шаге 3 / в SMS приходит битым.
        phone_country: '',
        password: '',
        password_confirmation: '',
        // Designer-only
        company_name: '',
        website_link: '',
        portfolio_link: '',
        // Consent
        agree: false,
    };
}

Alpine.data('registrationWizard', () => ({
    currentStep: 1,
    userType: null,
    formData: emptyFormData(),
    code: '',
    redirectUrl: '/',
    submitting: false,
    errors: {}, // field -> message
    resendSeconds: 0,
    _resendTimer: null,
    // DEV-993/994: нормализованный E.164 номер для отправки/отображения.
    // Держим ОТДЕЛЬНО от formData.phone, который привязан к x-model инпута intl-tel-input
    // (separateDialCode). Если бы мы писали E.164 обратно в formData.phone, x-model вернул бы
    // «+код» внутрь инпута → intl-tel-input продублировал бы код страны при возврате назад (DEV-994).
    phoneE164: '',

    get isDesigner() { return this.userType === 'designer'; },
    // DEV-993: показываем номер ровно в том виде, в котором он уйдёт на бэкенд (E.164),
    // без захардкоженной российской маски +X (XXX) XXX-XX-XX, которая «съедала»/сдвигала разряды.
    get phoneDisplay() { return this.phoneE164 || this.formData.phone || ''; },
    get stepLabel() { return `STEP ${this.currentStep} / 3`; },
    get showStepHeader() { return this.currentStep <= 3; },
    get showBackButton() { return this.currentStep === 2 || this.currentStep === 3; },
    get resendLabel() {
        const m = Math.floor(this.resendSeconds / 60);
        const s = this.resendSeconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    },
    get canResend() { return this.resendSeconds === 0; },

    init() {
        // Глобальный триггер «открыть регистрацию» — слушаем document-событие,
        // чтобы существующие ссылки вида data-open="register" могли открыть wizard.
        document.addEventListener('register:open', () => this.open());

        // Cleanup timer-а после полного закрытия (анимация уже отыграла).
        // 'close' event срабатывает и при Esc/клике по бэкдропу, и при .close().
        // Reset state не нужен — он происходит в open() при следующем открытии.
        this.$refs.dialog?.addEventListener('close', () => {
            this._clearResendTimer();
        });

        // Автофокус при переходе между шагами регистрации:
        //   step 1 — выбор типа пользователя (нет инпутов, фокус не нужен)
        //   step 2 — поля формы (фокусим email через [data-autofocus])
        //   step 3 — SMS-код
        this.$watch('currentStep', () => {
            this.$nextTick(() => focusFirstInput(this.$refs.dialog));
        });

        // Снимаем ошибку поля, как только пользователь начал его исправлять.
        // Новые ошибки при этом не показываем — полная перевалидация только на submit
        // (см. _validateStep2). Так ошибка не «висит» до следующего нажатия NEXT STEP.
        const fields = [
            'first_name', 'last_name', 'email', 'phone', 'password',
            'password_confirmation', 'website_link', 'portfolio_link', 'agree',
        ];
        fields.forEach((field) => {
            this.$watch(`formData.${field}`, () => this._clearError(field));
        });
        // Шаг 3: ошибка SMS-кода живёт отдельно от formData.
        this.$watch('code', () => this._clearError('code'));
    },

    // Реактивно убирает одну ошибку (переприсваиваем объект, чтобы x-show обновился).
    _clearError(field) {
        if (!this.errors[field]) return;
        const next = { ...this.errors };
        delete next[field];
        this.errors = next;
    },

    open() {
        this.reset();
        openDialog(this.$refs.dialog);
    },

    close() {
        closeDialog(this.$refs.dialog);
    },

    reset() {
        this.currentStep = 1;
        this.userType = null;
        this.formData = emptyFormData();
        this.phoneE164 = '';
        this.code = '';
        this.errors = {};
        this.redirectUrl = '/';
        this.submitting = false;
        this._clearResendTimer();
    },

    back() {
        if (this.currentStep === 2) {
            this.currentStep = 1;
            this.userType = null;
        } else if (this.currentStep === 3) {
            this.currentStep = 2;
        }
    },

    _clearResendTimer() {
        if (this._resendTimer) {
            clearInterval(this._resendTimer);
            this._resendTimer = null;
        }
        this.resendSeconds = 0;
    },

    _startResendTimer() {
        this._clearResendTimer();
        this.resendSeconds = RESEND_TIMER_SECONDS;
        this._resendTimer = setInterval(() => {
            this.resendSeconds--;
            if (this.resendSeconds <= 0) this._clearResendTimer();
        }, 1000);
    },

    // ─── Step 1: user type ─────────────────────────────────────────────
    async selectUserType(type) {
        if (this.submitting) return;
        this.submitting = true;
        this.userType = type;

        try {
            await apiRequest('/register/step1', {
                method: 'POST',
                body: { user_type: type },
            });
            this.currentStep = 2;
        } catch (e) {
            console.error('Step 1 error:', e);
            window.showToast('error', e instanceof APIError ? e.message : 'Registration error. Please try again.');
            this.userType = null;
        } finally {
            this.submitting = false;
        }
    },

    // ─── Step 2: form data ─────────────────────────────────────────────

    /**
     * DEV-993/994: единственный источник истины для телефона — инстанс intl-tel-input.
     *
     * При separateDialCode код страны (+7, +39…) живёт в виджете флага, а внутри <input>
     * лежит ТОЛЬКО локальная часть. Поэтому:
     *  - formData.phone (то, что печатает x-model) = локальная часть без кода;
     *  - getNumber() возвращает полный E.164 (+код+номер) — собранный ровно один раз
     *    самим intl-tel-input, без ручной конкатенации (нет двойного кода при возврате назад);
     *  - getSelectedCountryData().iso2 — код страны для бэкенда.
     *
     * Записываем E.164 обратно в formData.phone, НО в input.value не пишем — иначе intl-tel-input
     * с separateDialCode воспринял бы «+код» как часть локального номера и продублировал код (DEV-994).
     */
    _syncPhoneFromIti() {
        const input = this.$refs.phoneInput;
        const iti = input && input._iti;
        if (!iti || typeof iti.getNumber !== 'function') {
            // intl-tel-input не инициализирован — fallback: шлём ровно то, что ввёл пользователь.
            this.phoneE164 = this.formData.phone;
            this.formData.phone_country = '';
            return;
        }

        // getNumber() собирает полный E.164 (+код+номер) ровно один раз самим intl-tel-input —
        // без ручной конкатенации, поэтому код страны не задваивается при возврате назад (DEV-994).
        const e164 = iti.getNumber(); // '' если номер пустой/невалидный
        this.phoneE164 = e164 || this.formData.phone;

        const country = iti.getSelectedCountryData ? iti.getSelectedCountryData().iso2 : '';
        this.formData.phone_country = country || '';
    },

    _validateStep2() {
        this.errors = {};

        if (!this.formData.first_name.trim()) this.errors.first_name = 'First name is required';
        if (!this.formData.last_name.trim()) this.errors.last_name = 'Last name is required';

        if (!this.formData.email || !EMAIL_REGEX.test(this.formData.email)) {
            this.errors.email = 'Please enter a valid email address';
        }

        const phoneValue = this.formData.phone.trim();
        if (!phoneValue) {
            this.errors.phone = 'Phone number is required';
        } else {
            const digitsOnly = phoneValue.replace(/[^\d]/g, '');
            if (phoneValue === '+' || digitsOnly.length < 7) {
                this.errors.phone = 'Please enter a valid phone number (at least 7 digits)';
            }
        }

        if (!this.formData.password || this.formData.password.length < 6) {
            this.errors.password = 'Password must be at least 6 characters';
        }
        if (this.formData.password !== this.formData.password_confirmation) {
            this.errors.password_confirmation = 'Passwords do not match';
        }

        if (this.isDesigner) {
            if (this.formData.website_link.trim() && !URL_REGEX.test(this.formData.website_link)) {
                this.errors.website_link = 'Please enter a valid website URL';
            }
            if (this.formData.portfolio_link.trim() && !URL_REGEX.test(this.formData.portfolio_link)) {
                this.errors.portfolio_link = 'Please enter a valid portfolio URL';
            }
        }

        if (!this.formData.agree) {
            this.errors.agree = 'Please agree to data processing';
        }

        return Object.keys(this.errors).length === 0;
    },

    async submitStep2() {
        if (this.submitting) return;
        // DEV-993: до валидации/отправки нормализуем телефон в E.164 и фиксируем страну.
        // После этого formData.phone и formData.phone_country едут на бэкенд и доходят до шага 3.
        this._syncPhoneFromIti();
        if (!this._validateStep2()) return;

        this.submitting = true;
        try {
            // Endpoint /register/step2 принимает form-encoded — используем FormData
            const formData = new FormData();
            formData.append('user_type', this.userType);
            for (const [key, value] of Object.entries(this.formData)) {
                if (key === 'agree') {
                    if (value) formData.append('agree', '1');
                } else if (key === 'phone') {
                    // DEV-993: на бэкенд уходит нормализованный E.164 (phoneE164),
                    // а не сырая локальная часть из инпута (formData.phone).
                    formData.append('phone', this.phoneE164);
                } else {
                    formData.append(key, value);
                }
            }

            const response = await apiRequest('/register/step2', {
                method: 'POST',
                body: formData,
            });

            if (!response || !response.success) {
                window.showToast('error', (response && response.message) || 'Registration failed. Please try again.');
                return;
            }

            this.currentStep = 3;
            this._startResendTimer();

            // Отправляем SMS-код асинхронно — не блокируем UI
            try {
                await apiRequest('/register/send-code', {
                    method: 'POST',
                    body: { phone: this.phoneE164 },
                });
            } catch (smsError) {
                console.error('SMS send error:', smsError);
                if (smsError instanceof APIError) {
                    window.showToast('error', smsError.message);
                }
            }
        } catch (e) {
            if (e instanceof APIError && e.status === 422 && e.details?.fields) {
                this.errors = {};
                Object.keys(e.details.fields).forEach(field => {
                    this.errors[field] = e.details.fields[field][0];
                });
            } else {
                console.error('Step 2 error:', e);
                window.showToast('error', e instanceof APIError ? e.message : 'Registration failed.');
            }
        } finally {
            this.submitting = false;
        }
    },

    // ─── Step 3: SMS OTP verification ──────────────────────────────────
    async submitStep3() {
        if (this.submitting) return;

        if (!this.code || this.code.length !== 4) {
            this.errors = { code: 'Please enter 4-digit code' };
            return;
        }

        this.submitting = true;
        this.errors = {};

        try {
            const response = await apiRequest('/register/verify', {
                method: 'POST',
                body: { code: this.code },
            });

            if (response && response.success) {
                this.redirectUrl = response.redirect || '/';
                this.currentStep = 4;
                this._clearResendTimer();
            } else {
                this.errors = { code: (response && response.message) || 'Invalid confirmation code' };
                this.code = '';
                this._startResendTimer();
            }
        } catch (e) {
            let errorMessage = 'Invalid confirmation code';
            if (e instanceof APIError) {
                if (e.details?.fields?.code) errorMessage = e.details.fields.code[0];
                else errorMessage = e.message;
            }
            this.errors = { code: errorMessage };
            this.code = '';
            this._startResendTimer();
        } finally {
            this.submitting = false;
        }
    },

    async resendCode() {
        if (this.submitting || !this.canResend) return;

        this.submitting = true;
        try {
            const response = await apiRequest('/register/resend', { method: 'POST' });
            if (response && response.success) {
                this._startResendTimer();
                this.errors = {};
                window.showToast('success', 'Verification code resent');
            }
        } catch (e) {
            console.error('Resend error:', e);
            window.showToast('error', e instanceof APIError ? e.message : 'Failed to resend code');
        } finally {
            this.submitting = false;
        }
    },

    // ─── Finish ────────────────────────────────────────────────────────
    finish() {
        window.location.href = this.redirectUrl || '/';
    },
}));
